// Shared "See the Impact" maths — used by the prospect deck and the builder preview.

export const ROI_DEFAULTS = {
  enabled: true,
  show_save: true,   // "Save money" view (their spend today vs NicheCom)
  show_make: true,   // "Make money" view (resell to vendors with an uplift)
  annual_visits_default: 100,
  visits_max: 10000,
  current_cost_per_visit: 150,
  base_bundle_index: null,
  nichecom_cost_per_visit: null,
  uplift_pct: 20,
  sqft: { enabled: false, rate: 0.025, threshold: 2500, avg_sqft: 1800, mode: "agency_pays" },
  floorplan: { enabled: false, price: 80, share_pct: 100, mode: "included" },
  epc: { enabled: false, price: 90, share_pct: 40, mode: "vendor_pays" },
};

export const EXTRA_MODE_LABELS = {
  included: "Included in package",
  agency_pays: "Agency pays",
  vendor_pays: "Vendor pays",
};

// Which calculator views are switched on, in display order
export const roiModes = (roi) => [
  ...(roi?.show_save !== false ? ["save"] : []),
  ...(roi?.show_make !== false ? ["make"] : []),
];
// The block only renders when it's on, has a view, and has a price to work from
export const roiVisible = (roi, bundles) =>
  roi?.enabled !== false && roiModes(roi).length > 0 &&
  ((bundles || []).length > 0 || Number(roi?.nichecom_cost_per_visit) > 0);

export const normaliseRoi = (roi) => ({
  ...ROI_DEFAULTS,
  ...(roi || {}),
  sqft: { ...ROI_DEFAULTS.sqft, ...(roi?.sqft || {}) },
  floorplan: { ...ROI_DEFAULTS.floorplan, ...(roi?.floorplan || {}) },
  epc: { ...ROI_DEFAULTS.epc, ...(roi?.epc || {}) },
});

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const bundleBaseCost = (bundle) => num(bundle?.price_without_epc);

export const fmtGBP = (v, dp = 0) =>
  `£${num(v).toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

export const fmtMoney = (v) => (Math.abs(num(v)) % 1 === 0 ? fmtGBP(v, 0) : fmtGBP(v, 2));

// Clamp visits to the slider range and snap to a "nice" step so dragging feels intentional.
export const snapVisits = (v, max) => {
  const clamped = Math.min(Math.max(Math.round(num(v, 1)), 1), max);
  const step = clamped < 20 ? 1 : clamped < 200 ? 5 : clamped < 2000 ? 10 : 50;
  return Math.min(Math.max(Math.round(clamped / step) * step, 1), max);
};

// Large-property surcharge is disclosed on the page, not modelled in the ROI maths
export const sqftNote = (roi) =>
  roi?.sqft?.enabled
    ? `Properties over ${num(roi.sqft.threshold).toLocaleString("en-GB")} sq ft: +£${num(roi.sqft.rate).toFixed(3)} per sq ft`
    : null;

export const buildExtras = (roi) => {
  const extras = [];
  if (roi.floorplan?.enabled) {
    const share = num(roi.floorplan.share_pct, 100) / 100;
    extras.push({
      key: "floorplan",
      label: "Floorplans",
      perVisit: num(roi.floorplan.price) * share,
      mode: roi.floorplan.mode || "included",
      detail: `${fmtMoney(roi.floorplan.price)} × ${Math.round(share * 100)}% of visits`,
    });
  }
  if (roi.epc?.enabled) {
    const share = num(roi.epc.share_pct, 40) / 100;
    extras.push({
      key: "epc",
      label: "EPCs",
      perVisit: num(roi.epc.price) * share,
      mode: roi.epc.mode === "vendor_pays" ? "vendor_pays" : "agency_pays",
      detail: `${fmtMoney(roi.epc.price)} × ${Math.round(share * 100)}% of visits`,
    });
  }
  return extras;
};

export function computeRoi({ roi, visits, baseCost, currentCost }) {
  const r = normaliseRoi(roi);
  const uplift = num(r.uplift_pct, 20) / 100;
  const base = num(baseCost);
  const extras = buildExtras(r);
  const agencyExtras = extras.filter((e) => e.mode === "agency_pays").reduce((s, e) => s + e.perVisit, 0);
  const vendorExtras = extras.filter((e) => e.mode === "vendor_pays");
  const vendorExtrasPerVisit = vendorExtras.reduce((s, e) => s + e.perVisit, 0);
  const chargeablePerVisit = base + agencyExtras + vendorExtrasPerVisit;

  const currentPerVisit = num(currentCost);
  const nichecomPerVisit = base + agencyExtras;
  const offsetPerVisit = vendorExtrasPerVisit * uplift;
  const offsetLabel =
    vendorExtras.length === 1 && vendorExtras[0].key === "epc"
      ? "Offset from vendor-funded EPCs"
      : "Offset from vendor-funded extras";

  return {
    uplift,
    base,
    extras,
    agencyExtras,
    vendorExtras,
    chargeablePerVisit,
    save: {
      currentPerVisit,
      currentAnnual: currentPerVisit * visits,
      nichecomPerVisit,
      nichecomAnnual: nichecomPerVisit * visits,
      offsetPerVisit,
      offsetAnnual: offsetPerVisit * visits,
      offsetLabel,
      saving: (currentPerVisit - nichecomPerVisit + offsetPerVisit) * visits,
    },
    make: {
      costPerVisit: chargeablePerVisit,
      vendorChargePerVisit: chargeablePerVisit * (1 + uplift),
      marginPerVisit: chargeablePerVisit * uplift,
      annualMargin: chargeablePerVisit * uplift * visits,
    },
  };
}
